
import Vue from "vue";
import Component from "vue-class-component";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import {
    PracticeListActions,
    PracticesClient, PracticeType, PracticeViewModel, ValidityStatus
} from "@/api-client";
import {Prop} from "vue-property-decorator";
import {BAlert, BButton, BModal, BCollapse, BSpinner, VBToggle} from 'bootstrap-vue';
import toasts from "@/components/Toasts";
import userInfo from "@/features/shared/components/UserInfo";
import FilterCheckbox from "@/components/FilterCheckbox.vue";
import CardQuicklink from "@/features/dashboard/components/CardQuicklink.vue";
import {NavigationGuardNext} from "vue-router/types/router";
import PracticeTable from "@/features/personProfile/components/PracticeTable.vue";
import AddPracticeModal from "@/features/documents/practices/components/AddPracticeModal.vue";
import { $ls } from "@chenfengyuan/vue-qrcode";

class Filters {
    showApprovedAndNotExpired = true;
    showRejectedOrExpired = false;
    showUnchecked = true;
}

interface Props {
    seafarerId: string;
    practices: PracticeViewModel[];
    availableActions: PracticeListActions;
}

@Component({
    components: {
        PracticeTable,
        SectionPageHeading,
        FilterCheckbox,
        CardQuicklink,
        AddPracticeModal
    },
    beforeRouteEnter(to, from, next: NavigationGuardNext<PracticeList>) {
        const props = to.meta!.props as Props;
        props.seafarerId = to.params.seafarerId;
        Promise.all([
            new PracticesClient().list(to.params.seafarerId),
            new PracticesClient().availableListActions(to.params.seafarerId)
        ]).then(values => {
            props.practices = values[0];
            props.availableActions = values[1];
            next();
        }).catch(e => next(e));
    }
})

export default class PracticeList extends Vue implements Props {
    @Prop({required: true})
    seafarerId!: string;
    @Prop({required: true})
    practices!: PracticeViewModel[];
    @Prop({required: true})
    availableActions!: PracticeListActions;

    filters = new Filters();
    userInfo = userInfo;
    isImporting = false;

    $refs!: {
        addPracticeModal: AddPracticeModal;
    }

    // Define all practice types with their values and heading keys
    allPracticeTypes = [
        { value: PracticeType.Seafaring, headingKey: 'PersonProfile_SeafaringPractices_Heading' },
        { value: PracticeType.ShipRepair, headingKey: 'PersonProfile_ShipRepairPractices_Heading' },
        { value: PracticeType.InlandVessel, headingKey: 'PersonProfile_InlandVesselPractices_Heading' },
        { value: PracticeType.Pilotage, headingKey: 'PersonProfile_PilotagePractices_Heading' },
        { value: PracticeType.VtsCenter, headingKey: 'PersonProfile_VtsCenterPractices_Heading' }
    ];

    openNewPracticeModal() {
        this.$refs.addPracticeModal.open(this.seafarerId);
    }

    getaddShipPracticeHref() {
        return `${window.location.pathname}/add-ship-repair`
    }

    getPracticeHeading(practiceType: any): string {
        return (this.$ls as any)[practiceType.headingKey] || '';
    }

    async importFromEmde() {
        this.isImporting = true;
        try {
            const result = await new PracticesClient().importFromEmde(this.seafarerId);
            toasts.success(this.$ls.PersonProfile_Practices_Toast_EmdeImportSuccess
                .replace('{0}', String(result.importedCount))
                .replace('{1}', String(result.skippedDuplicateCount)));
            // Reload practices list
            this.practices = await new PracticesClient().list(this.seafarerId);
        } catch (e) {
            toasts.failure(this.$ls.PersonProfile_Practices_Toast_EmdeImportFailed);
        } finally {
            this.isImporting = false;
        }
    }

    getFilteredPracticesByType(practiceType: any): PracticeViewModel[] {
        return this.practices
            .filter(i => i.type == practiceType.value)
            .filter(item =>
                this.filters.showApprovedAndNotExpired && item.status == ValidityStatus.Valid
                || this.filters.showRejectedOrExpired && item.status == ValidityStatus.Invalid
                || this.filters.showUnchecked && item.status == ValidityStatus.Unchecked);
    }

}
